import { formatShortDate, parseIsoDate } from '../listing-model';

/**
 * "What {business} will see" (spec §5.3): one line per section, built from the payloads the
 * applicant is typing right now. Pure so the lines, states and truncation-free wording are tested.
 */
export type ApplyStepKey =
  | 'move_in' | 'household' | 'about_you' | 'employment' | 'residential_history' | 'custom_questions' | 'attachments' | 'review';

export type CardSectionKey = 'moveIn' | 'contact' | 'work' | 'homes' | 'extras';
export type CardLineState = 'done' | 'current' | 'todo';

export interface CardLine {
  key: CardSectionKey;
  label: string;
  value: string;
  state: CardLineState;
  /** The step "Edit" jumps to: the section's first step in the flow. */
  step: ApplyStepKey;
}

export const cardSections: ReadonlyArray<{ key: CardSectionKey; label: string; steps: readonly ApplyStepKey[]; placeholder: string }> = [
  { key: 'moveIn', label: 'Move-in', steps: ['move_in', 'household'], placeholder: 'When you’ll move in' },
  { key: 'contact', label: 'Contact', steps: ['about_you'], placeholder: 'Email and phone' },
  { key: 'work', label: 'Work', steps: ['employment'], placeholder: 'Where you work' },
  { key: 'homes', label: 'Homes', steps: ['residential_history'], placeholder: 'Where you’ve lived' },
  { key: 'extras', label: 'Extras', steps: ['custom_questions', 'attachments'], placeholder: 'Questions and documents' },
];

type Payload = Record<string, unknown> | undefined;

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

/** "(512) 555-0148" for ten US digits; anything else as typed. */
export function formatPhone(value: unknown): string {
  const raw = text(value);
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return local.length === 10 ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}` : raw;
}

/** Keeps the stored phone exactly as the shared phone input did: digits, with a leading plus. */
export function normalizePhone(raw: string): string {
  const trimmed = raw.trim();
  const plus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '').slice(0, 15);
  if (!digits) return plus ? '+' : '';
  return plus ? `+${digits}` : digits;
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

/** A yearly income as a month, rounded to the dollar; null when it isn't a usable number. */
export function monthlyIncome(annual: unknown): number | null {
  const value = typeof annual === 'number' ? annual : typeof annual === 'string' && annual.trim() ? Number(annual) : NaN;
  return Number.isFinite(value) && value > 0 ? Math.round(value / 12) : null;
}

/** The hint under the yearly income: "About $7,200 a month". */
export function describeMonthly(annual: unknown): string | null {
  const month = monthlyIncome(annual);
  return month === null ? null : `About ${formatMoney(month)} a month`;
}

const jobTypeWords: Record<string, string> = {
  Employed: 'Employed', SelfEmployed: 'Self-employed', Unemployed: 'Not working', Student: 'Student', Other: 'Other income',
};

function rows(payload: Payload, key: string): Array<Record<string, unknown>> {
  const value = payload?.[key];
  return Array.isArray(value) ? value.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object') : [];
}

export function moveInValue(moveIn: Payload, household: Payload, isCoSigner: boolean, today: Date): string {
  const date = parseIsoDate(text(moveIn?.expected_move_in_date));
  const parts = [date ? formatShortDate(date, today) : null];
  if (isCoSigner) parts.push('co-signing');
  else parts.push(household?.moving_with === 'WITH_CO_APPLICANTS' ? 'living here with others' : 'living here alone');
  return parts.filter(Boolean).join(' · ');
}

export function contactValue(aboutYou: Payload, fallbackEmail = ''): string {
  const name = [text(aboutYou?.first_name), text(aboutYou?.last_name)].filter(Boolean).join(' ');
  const email = text(aboutYou?.email) || fallbackEmail.trim();
  const phone = formatPhone(aboutYou?.phone);
  return [email || name, phone].filter(Boolean).join(' · ');
}

/** "Registered nurse, St. David's · $7,200 / mo": the first job, then all income as a month. */
export function workValue(employment: Payload): string {
  const jobs = rows(employment, 'employments');
  const list = jobs.length > 0 ? jobs : employment && (employment.job_type || employment.title) ? [employment] : [];
  const usable = list.filter((job) => text(job.job_type) || text(job.title) || text(job.company_name));
  if (usable.length === 0) return '';
  const first = usable[0];
  const role = [text(first.title), text(first.company_name)].filter(Boolean).join(', ') || jobTypeWords[text(first.job_type)] || '';
  const monthly = usable.reduce((sum, job) => sum + (monthlyIncome(job.annual_income) ?? 0), 0);
  const more = usable.length > 1 ? ` and ${usable.length - 1} more` : '';
  return [`${role}${more}`, monthly > 0 ? `${formatMoney(monthly)} / mo` : null].filter(Boolean).join(' · ');
}

/** "1408 Alameda Dr and 1 more". */
export function homesValue(residences: Payload): string {
  const homes = rows(residences, 'residences').filter((row) => text(row.address_line_1) || text(row.city));
  if (homes.length === 0) return '';
  const first = text(homes[0].address_line_1) || text(homes[0].city);
  return homes.length > 1 ? `${first} and ${homes.length - 1} more` : first;
}

export function answeredCount(customQuestions: Payload): number {
  return rows(customQuestions, 'answers').filter((row) => {
    const answer = row.answer ?? row.value;
    if (answer === null || answer === undefined) return false;
    if (typeof answer === 'string') return answer.trim().length > 0;
    if (Array.isArray(answer)) return answer.length > 0;
    return true;
  }).length;
}

export function extrasValue(customQuestions: Payload, documents: number, hasQuestions: boolean): string {
  const parts = [hasQuestions ? plural(answeredCount(customQuestions), 'answer') : null, plural(documents, 'document')];
  return parts.filter(Boolean).join(' · ');
}

export interface ApplicationCardInput {
  /** The flow's steps as the applicant sees them (co-signers have no household, etc.). */
  visibleSteps: readonly ApplyStepKey[];
  currentStep: ApplyStepKey;
  completed: (step: ApplyStepKey) => boolean;
  payloads: Partial<Record<ApplyStepKey, Payload>>;
  isCoSigner: boolean;
  documents: number;
  email?: string;
  today: Date;
}

export function applicationCardLines(input: ApplicationCardInput): CardLine[] {
  const { visibleSteps, currentStep, completed, payloads, isCoSigner, documents, today } = input;
  const hasQuestions = visibleSteps.includes('custom_questions');
  const values: Record<CardSectionKey, string> = {
    moveIn: moveInValue(payloads.move_in, payloads.household, isCoSigner, today),
    contact: contactValue(payloads.about_you, input.email),
    work: workValue(payloads.employment),
    homes: homesValue(payloads.residential_history),
    extras: extrasValue(payloads.custom_questions, documents, hasQuestions),
  };

  return cardSections.flatMap((section) => {
    const steps = section.steps.filter((step) => visibleSteps.includes(step));
    if (steps.length === 0) return [];
    const isCurrent = steps.includes(currentStep);
    const done = steps.every((step) => completed(step));
    const state: CardLineState = isCurrent ? 'current' : done ? 'done' : 'todo';
    // Saved but empty sections still say so once done: the business sees nothing there.
    const value = state === 'todo' ? section.placeholder
      : values[section.key] || (state === 'done' ? (section.key === 'homes' ? 'None added' : 'Nothing added') : section.placeholder);
    return [{ key: section.key, label: section.label, value, state, step: steps[0] }];
  });
}

export interface ScreeningFacts {
  requiresScreening?: boolean | null;
  screeningFeeCents?: number | null;
  screeningFeePayer?: string | null;
}

/**
 * The sign step's Checkr line (spec §5.4), from the unit's screening facts. Nothing is said
 * unless the business requires screening and the facts are known.
 */
export function screeningNotice(facts: ScreeningFacts | null | undefined, businessName: string): string | null {
  if (!facts || facts.requiresScreening !== true) return null;
  if (facts.screeningFeePayer === 'organization') {
    return `Next you’ll verify your identity with Checkr; ${businessName || 'the rental business'} covers the fee.`;
  }
  if (facts.screeningFeePayer === 'applicant') {
    const cents = facts.screeningFeeCents;
    if (typeof cents !== 'number' || !Number.isSafeInteger(cents) || cents < 0) return null;
    if (cents === 0) return 'Next you’ll verify your identity with Checkr.';
    const fee = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
    return `Next you’ll verify your identity with Checkr and pay them the ${fee} screening fee.`;
  }
  return null;
}
